import type { TFunction } from "i18next";
import { useMemo } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000ComboBoxBase from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U3148RoleActionHandler } from "./U3148RoleActionHandlers";
import type { U3148LogicalUnit, U3148LogicalUnitAction } from "../../../../U3148Service";

// Typage des props du composant
interface U3148RoleActionDlgProps {
  u3148RoleActionHandler: U3148RoleActionHandler;
  t: TFunction;
  NS: string;
}

// Composant pour le dialogue d'édition des actions d'une unité logique
function U3148RoleActionEditDlg({ u3148RoleActionHandler, t, NS }: U3148RoleActionDlgProps) {
  const { formStateHandlers } = u3148RoleActionHandler;

  const filteredLogicalUnitActions = useMemo(
    () =>
      u3148RoleActionHandler.logicalUnits.filter(
        (lu) => !u3148RoleActionHandler.gridLogicalUnitCodes.includes(lu.logicalUnitCode) || (formStateHandlers.getData() && lu.logicalUnitCode === formStateHandlers.getData().logicalUnitCode)
      ),
    [u3148RoleActionHandler.logicalUnits, u3148RoleActionHandler.gridLogicalUnitCodes, formStateHandlers]
  );

  // Calcul de l'état de la checkbox "Tout sélectionner"
  const areAllActionsChecked = useMemo(
    () =>
      u3148RoleActionHandler.logicalUnitActions.length > 0 &&
      u3148RoleActionHandler.logicalUnitActions.every((action) => action.idChecked),
    [u3148RoleActionHandler.logicalUnitActions]
  );

  // renmder
  return (
    <U2000SaveCancelDialog
      width="ax-w-2xl"
      height="h-[600px]"
      isOpen={u3148RoleActionHandler.isDialogOpen}
      onClose={u3148RoleActionHandler.onDlgClose}
      title={t("role.lblLogicalUnitAction", { ns: NS })}
      isValid={formStateHandlers.isValid()}
    >
      <div className="flex flex-col gap-y-4">
        <U2000ComboBoxBase
          data={filteredLogicalUnitActions}
          keyField="id"
          descField="logicalUnitDescription"
          onChangeObject={u3148RoleActionHandler.onLogicalUnitChangeObject}
          value={u3148RoleActionHandler.formStateHandlers.getData() as U3148LogicalUnit}
          disabled={u3148RoleActionHandler.formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT}
        />

        {u3148RoleActionHandler.logicalUnitActions && u3148RoleActionHandler.logicalUnitActions.length > 0 && (
          <div className="mb-2 flex items-center gap-x-2 border-b pb-2">
            <div
              role="checkbox"
              aria-checked={areAllActionsChecked}
              tabIndex={0}
              className={`h-6 w-6 cursor-pointer rounded-sm border-2 transition-colors duration-300 ${areAllActionsChecked ? "border-blue-500 bg-blue-100" : "border-gray-300 bg-white"}`}
              onClick={() => u3148RoleActionHandler.onCheckAllLogicalUnitActions()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  u3148RoleActionHandler.onCheckAllLogicalUnitActions();
                }
              }}
            >
              {areAllActionsChecked && (
                <svg className="m-auto h-5 w-5 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <p className="inline-block font-semibold text-gray-800">{t("role.lblSelectAll", { ns: NS })}</p>
          </div>
        )}

        {u3148RoleActionHandler.logicalUnitActions &&
          u3148RoleActionHandler.logicalUnitActions.map((action: U3148LogicalUnitAction) => (
            <div key={action.id} className="mb-1 flex items-center gap-x-2">
              <div
                role="checkbox"
                aria-checked={action.idChecked}
                tabIndex={0}
                className={`h-6 w-6 cursor-pointer rounded-sm border-2 transition-colors duration-300 ${action.idChecked ? "border-blue-500 bg-blue-100" : "border-gray-300 bg-white"}`}
                onClick={() => u3148RoleActionHandler.onCheckLogicalUnitAction(action)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    u3148RoleActionHandler.onCheckLogicalUnitAction(action);
                  }
                }}
              >
                {action.idChecked && (
                  <svg className="m-auto h-5 w-5 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <p className="inline-block text-gray-800">
                {action.logicalUnitActionCode} - {action.logicalUnitActionDescription}
              </p>
            </div>
          ))}
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3148RoleActionEditDlg;
